import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Instagram, Linkedin, Mail, Globe, MessageCircle, Send, Loader2 } from "lucide-react";
import { Reveal, SectionLabel, MaskedLine } from "@/components/Reveal";
import { LogoNavy } from "@/components/Layout";
import { DIAG_URL, WPP_URL, EMAIL_URL, INSTA_URL, LINKEDIN_URL, SITE_URL } from "@/links";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { icon: Mail, label: "E-mail", value: "atendimento@fuchsbrand.com.br", href: EMAIL_URL, testid: "channel-email" },
  { icon: MessageCircle, label: "WhatsApp", value: "47 99253-6961", href: WPP_URL, testid: "channel-whatsapp" },
  { icon: Instagram, label: "Instagram", value: "@fuchs.brand_", href: INSTA_URL, testid: "channel-instagram" },
  { icon: Linkedin, label: "LinkedIn", value: "/in/danyfuchs4D", href: LINKEDIN_URL, testid: "channel-linkedin" },
  { icon: Globe, label: "Site", value: "fuchsbrand.com.br", href: SITE_URL, testid: "channel-site" },
];

const START_STEPS = [
  {
    n: "1",
    t: "Diagnóstico de Maturidade 4D",
    d: "Você mede a maturidade da empresa nas cinco dimensões e gera o radar estratégico.",
  },
  {
    n: "2",
    t: "Reunião estratégica de 30 minutos",
    d: "Reunião executiva, sem custo, para analisar o radar, esclarecer o modelo de negócio e calibrar o escopo da proposta.",
  },
  {
    n: "3",
    t: "Formalização e início",
    d: "Cronograma operacional, acesso ao painel do cliente e agenda das primeiras entrevistas com sócios e lideranças.",
  },
];

const inputCls =
  "w-full border border-white/15 bg-ink/60 px-5 py-4 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-brand-orange";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">{label}</span>
    {children}
  </label>
);

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", whatsapp: "", challenge: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensagem enviada. A Danielle retorna em breve.");
      setForm({ name: "", company: "", email: "", whatsapp: "", challenge: "" });
    } catch (err) {
      toast.error("Não foi possível enviar. Tente pelo WhatsApp ou e-mail direto.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Nome" testid="field-name">
          <input
            required
            value={form.name}
            onChange={set("name")}
            data-testid="contact-input-name"
            className={inputCls}
            placeholder="Seu nome"
          />
        </Field>
        <Field label="Empresa" testid="field-company">
          <input
            value={form.company}
            onChange={set("company")}
            data-testid="contact-input-company"
            className={inputCls}
            placeholder="Nome da empresa"
          />
        </Field>
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="E-mail" testid="field-email">
          <input
            required
            type="email"
            value={form.email}
            onChange={set("email")}
            data-testid="contact-input-email"
            className={inputCls}
            placeholder="voce@empresa.com.br"
          />
        </Field>
        <Field label="WhatsApp" testid="field-whatsapp">
          <input
            value={form.whatsapp}
            onChange={set("whatsapp")}
            data-testid="contact-input-whatsapp"
            className={inputCls}
            placeholder="(47) 99999-0000"
          />
        </Field>
      </div>
      <Field label="Qual é o desafio da sua marca hoje?" testid="field-challenge">
        <textarea
          required
          rows={5}
          value={form.challenge}
          onChange={set("challenge")}
          data-testid="contact-input-challenge"
          className={`${inputCls} resize-none`}
          placeholder="Conte em poucas linhas o momento da sua empresa."
        />
      </Field>
      <button
        type="submit"
        disabled={sending}
        data-testid="contact-form-submit-button"
        className="group flex w-full items-center justify-center gap-3 bg-brand-orange px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-brand-red disabled:opacity-60"
      >
        {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        {sending ? "Enviando..." : "Enviar mensagem"}
      </button>
    </form>
  );
};

export default function Contato() {
  return (
    <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
      <section data-testid="contact-hero" className="hero-glow noise relative overflow-hidden bg-ink pb-20 pt-36 lg:pb-28 lg:pt-48">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.15 }}>
            <SectionLabel>Contato</SectionLabel>
          </motion.div>
          <h1 className="mt-8 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>Vamos conversar sobre</MaskedLine>
            <MaskedLine delay={0.4}>
              a <span className="font-serif font-medium italic text-brand-orange">direção</span> da sua marca.
            </MaskedLine>
          </h1>
          <motion.p
            className="mt-8 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            Escreva para a FUCHS, chame no WhatsApp ou comece pelo diagnóstico.
          </motion.p>
        </div>
      </section>

      <section data-testid="contact-main" className="bg-ink pb-24 lg:pb-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-7">
            <div className="border border-white/10 bg-navy/40 p-8 backdrop-blur-sm lg:p-12">
              <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Envie uma mensagem</h2>
              <div className="mt-8">
                <ContactForm />
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">Canais diretos</h2>
              <div className="mt-8 space-y-4">
                {CHANNELS.map((c) => (
                  <a
                    key={c.label}
                    href={c.href}
                    target={c.href.startsWith("mailto") ? undefined : "_blank"}
                    rel="noopener noreferrer"
                    data-testid={c.testid}
                    className="group flex items-center gap-5 border border-white/10 p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-orange/60"
                  >
                    <span className="flex h-11 w-11 items-center justify-center border border-white/15 text-brand-orange transition-colors duration-300 group-hover:border-brand-orange">
                      <c.icon size={18} />
                    </span>
                    <span className="flex-1">
                      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{c.label}</span>
                      <span className="block text-sm font-medium text-white">{c.value}</span>
                    </span>
                    <ArrowUpRight size={16} className="text-slate-600 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-brand-orange" />
                  </a>
                ))}
              </div>
              <Link
                to={DIAG_URL}
                data-testid="contact-cta-diagnostico"
                className="group mt-8 flex items-center justify-center gap-3 border border-brand-orange/60 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-brand-orange transition-colors duration-300 hover:bg-brand-orange hover:text-white"
              >
                Fazer o diagnóstico
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="contact-steps" className="bg-paper py-24 text-navy lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <LogoNavy className="mb-8" />
            <SectionLabel light>Próximos passos</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Como começa o trabalho.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-10 md:grid-cols-3">
            {START_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div data-testid={`start-step-${s.n}`} className="group h-full border-t-2 border-navy/15 pt-8 transition-colors duration-500 hover:border-brand-orange">
                  <span className="font-display text-5xl font-extrabold text-navy/15 transition-colors duration-500 group-hover:text-brand-orange">
                    {s.n}
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold sm:text-2xl">{s.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-navy/70">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16 text-center">
            <a
              href={WPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-final-whatsapp-cta"
              className="group inline-flex items-center gap-3 bg-navy px-10 py-5 font-mono text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-brand-orange"
            >
              Fale com a Fuchs
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </motion.main>
  );
}
